/* eslint-disable jsdoc/valid-types */
/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
/* eslint-disable no-param-reassign */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ALL,
  getBookmarksDisplayAmount,
  RECOMMENDATIONS_AMOUNT,
  AUTHENTICATED,
  UNAUTHENTICATED,
} from 'lib/myNewsConstants';
import { produce } from 'immer';
import { useBedrockRegistration } from './bedrockRegistration';
import { myNewsApi as api } from './utils';

/**
 * Merges two bookmark arrays, ensuring each contentId appears only once.
 * @template {{ contentId: string }} T
 * @param {Array<T>}  oldArr - Existing bookmark list in state.
 * @param {Array<T>}  newArr - New bookmark list from the API.
 * @returns {Array<T>} - Combined list containing exactly one entry per unique contentId, preserving the latest version of each item.
 */
const mergeUniqueById = (oldArr = [], newArr = []) => {
  const map = new Map();
  [...oldArr, ...newArr].forEach((item) => map.set(item.contentId, item));
  return Array.from(map.values());
};

/**
 * Opens the authentication modal by setting its state to true.
 * Utilizes the Bedrock registration store to trigger the login modal. To be used as fallback
 */
const launchLoginModal = () => {
  useBedrockRegistration.getState().setAuthenticationModal(true);
};

/**
 * BookmarkData - inferred from usage.  More properties might be present in actual data.
 * @typedef {object} BookmarkData
 * @property {string} contentId - The ID of the content.
 * @property {string | undefined} contentType - The ID of the content.
 */

export const INITIAL_STATE = {
  user: {
    /** @type {string | undefined} */
    givenName: undefined,
  },
  authenticationState: '',
  bookmarks: {},
  /** @type {unknown} */
  token: undefined,
  filterType: ALL,
  customer: {
    zipCode: '',
    customerId: '',
    mparticleId: '',
    history: {
      /** @type unknown[] */
      data: [],
      pageInfo: {
        nextPage: null,
      },
    },
    bookmarks: {
      /** @type BookmarkData[] */
      data: [],
      pageInfo: {
        nextPage: null,
      },
    },
    recommendations: {
      data: [],
    },
  },
  loading: true,
  isBedrockApiEnabled: undefined,
};


/**
 * Store configuration for managing user authentication, customer data, bookmarks, and other related states.
 *
 * @param {Function} set - Function to update the store state.
 * @param {() => ReturnType<typeof store>} get - The function to get the current state.
 */
export const store = (set, get) => ({
  ...INITIAL_STATE,

  /**
   * Refresh Bedrock token (when enabled) and sync auth state.
   * @returns {Promise<boolean>} true if authenticated, false otherwise
   */
  ensureAuthenticated: async () => {
    if (!get().isBedrockApiEnabled) return true;

    try {
      await useBedrockRegistration.getState().refreshUserToken();
    } catch {
      // treat as unauthenticated
    }

    const authState = useBedrockRegistration.getState().authenticationState;
    set({ authenticationState: authState }, false, 'syncAuthState');
    return authState === AUTHENTICATED;
  },

  /**
   * Authenticates the user using the HFSapi identity service.

   * @param {object} options - The options for the authentication.
   * @param {string} [options.defaultPage] - The default page to redirect to after authentication.
   */
  authenticate: ({ defaultPage } = { defaultPage: undefined }) => {
    window?.HFSapi?.identity?.authenticate({ defaultPage });
  },

  /**
   * Updates the store with the new authentication state,
   * user data and token.
   */
  setAuthentication: () => {
    const { authenticationState, user } = window.HFSapi?.identity ?? {
      authenticationState: undefined,
      user: undefined,
    };
    set(
      {
        authenticationState,
        user,
      },
      false,
      'setAuthentication',
    );
  },

  /**
   * Logs out the user by resetting the authentication state, user data, and token in the store to their initial values.
   */
  resetAuthentication: () => {
    set(
      {
        authenticationState: UNAUTHENTICATED,
        bookmarks: INITIAL_STATE.bookmarks,
        customer: INITIAL_STATE.customer,
        token: INITIAL_STATE.token,
        user: INITIAL_STATE.user,
      },
      false,
      'resetAuthentication',
    );
  },

  /**
   * Resets the my news data in the store to its initial state.
   */
  reset: () => set({ ...INITIAL_STATE }, false, 'reset'),

  /**
   * Makes an API call to retrieve the customer data.
   *
   * @async
   */
  getCustomer: async () => {
    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api('/mynewsapi/customer/', {
      isBedrockApiEnabled: get().isBedrockApiEnabled,
    });
    set({
      customer: {
        ...get().customer,
        ...response.data.getCustomer,
      },
    }, false, 'getCustomer');
  },

  /**
   * Sets the loading state in the store.
   *
   * @param {boolean} loading - The loading state.
   */
  setLoading: (loading) => set({ loading }, false, 'setLoading'),

  /**
   * Sets the filter type in the store.
   *
   * @param {string} filterType - The filter type. Equals the content type.
   * Can be 'all', 'article', 'video', 'recipe', 'product'.
   */
  setFilterType: (filterType) => set({ filterType }, false, 'setFilterType'),

  /**
   * Retrieves the user's bookmarks from the server.
   * This is used for the mynews and accounts section.
   *
   * @param {object} params - The parameters for the get operation.
   * @param {number} [params.size] - The number of bookmarks to retrieve.
   * @param {string} [params.cursor] - The cursor for pagination.
   * @param {string} [params.filterType] - The filter type. Equals the content type.
   * Can be 'all', 'article', 'video', 'recipe', 'product'.
   * @param {string} [params.customerId] - The customer ID.
   * @async
   */
  getBookmarks: async ({
    size = getBookmarksDisplayAmount(),
    cursor,
    filterType = ALL,
    customerId,
  } = {}) => {
    set({ loading: true });

    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(
      `/mynewsapi/bookmarks/${size}/${cursor ?? null}/${filterType}`,
      { customerId, isBedrockApiEnabled: get().isBedrockApiEnabled },
    );

    const {
      data: {
        getCustomer: { bookmarks },
      },
    } = response;

    set(
      {
        customer: {
          ...get().customer,
          bookmarks: {
            data: mergeUniqueById(
              cursor ? get().customer.bookmarks.data : [],
              bookmarks.data,
            ),
            pageInfo: bookmarks.pageInfo,
          },
        },
        filterType,
        loading: false,
      },
      false,
      'getBookmarks',
    );
  },

  /**
   * Makes an API call to save the customer's zip code.
   *
   * @param {object} params - The parameters for the save operation.
   * @param {string} [params.zipcode] - The zip code to save.
   * @async
   */
  saveCustomer: async ({ zipcode } = {}) => {
    if (!zipcode) return;
    await api(
      `/mynewsapi/savecustomer/${zipcode}`,
      { },
    );
    await get().getCustomer();
  },

  /**
   * Retrieves a list of recommended items for the user from the server and updates the store with the result.
   *
   * @param {object} params - The parameters for the get operation.
   * @param {number} [params.size] - The number of recommendations to retrieve.
   * @async
   */
  getRecommendations: async ({ size = RECOMMENDATIONS_AMOUNT } = {}) => {
    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(
      `/mynewsapi/recommendations/${size}`,
      {
        isBedrockApiEnabled: get().isBedrockApiEnabled,
      },
    );
    set(produce((/** @type {MyNewsState} */ draft) => {
      draft.customer.recommendations.data = response.data.getCustomer.recommendations.data;
    }), false, 'getRecommendations');
  },

  /**
   * Retrieves the user's search history from the server and updates the store with the result.
   *
   * @param {object} params - The parameters for the get operation.
   * @param {number} [params.size] - The number of search history items to retrieve.
   * @param {string | null} [params.cursor] - The cursor for pagination.
   * @async
   */
  getHistory: async (
    {
      size = getBookmarksDisplayAmount(),
      cursor = null,
    } = {},
  ) => {
    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(
      `/mynewsapi/history/${size}/${cursor}`,
      {
        isBedrockApiEnabled: get().isBedrockApiEnabled,
      },
    );
    set(produce((/** @type {MyNewsState} */ draft) => {
      // Merge new data with existing data
      if (cursor) {
        draft.customer.history.data = [
          ...draft.customer.history.data,
          ...response.data.getCustomer.history.data,
        ];
      } else {
        draft.customer.history.data = response.data.getCustomer.history.data;
      }
      // Update pageInfo
      draft.customer.history.pageInfo = response.data.getCustomer.history.pageInfo;
    }), false, 'getHistory');
  },

  /**
   * Saves the given content ID to the user's search history.
   *
   * @param {object} params - The parameters for the get operation.
   * @param {string} params.contentId - The ID of the content to save.
   * @param {string} params.contentType - The type of the content to save.
   * @async
   */
  saveHistory: async ({
    contentType,
    contentId,
  }) => {
    set({ savedContentId: contentId }, false, 'saveHistory');
    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(
      `/mynewsapi/savehistory/${contentId}/${contentType}`,
      {
        isBedrockApiEnabled: get().isBedrockApiEnabled,
      },
    );
    return response.data.saveHistory;
  },

  /**
   * Creates a bookmark for the given content ID and content type.
   *
   * @param {object} params - The parameters for the create operation.
   * @param {string} params.contentId - The ID of the content to create the bookmark for.
   * @param {string} [params.contentType] - The type of the content to create the bookmark for.
   * @param {string} [params.customerId] - The customer ID.
   * @param {() => void} [params.fallback] - Fallback function to call if the user is not authenticated.
   * @async
   */
  createBookmark: async ({
    contentId,
    contentType,
    customerId,
    fallback = launchLoginModal,
  } = { contentId: 'undefined' }) => {
    if (!(await get().ensureAuthenticated())) {
      fallback();
      return false;
    }

    // optimistic flag
    set(
      (/** @type {MyNewsState} */ state) => ({
        bookmarks: { ...state.bookmarks, [contentId]: true },
      }),
      false,
      'createBookmarkOptimistic',
    );

    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(
      `/mynewsapi/createbookmark/${contentId}/${contentType}`,
      { customerId, isBedrockApiEnabled: get().isBedrockApiEnabled },
    );
    const { data } = response;

    if (!data.createBookmark) {
      set(
        (/** @type {MyNewsState} */ state) => ({
          bookmarks: { ...state.bookmarks, [contentId]: false },
        }),
        false,
        'createBookmarkRollback',
      );
      return false;
    }

    // inject into visible list for instant UI feedback
    set(
      produce((/** @type {MyNewsState} */ draft) => {
        draft.customer.bookmarks.data.unshift({ contentId, contentType });
      }),
      false,
      'createBookmarkAddToList',
    );

    return true;
  },


  /**
   * Deletes a bookmark for the given content ID.
   *
   * @param {object} params
   * @param {string} params.contentId - The ID of the content to delete the bookmark for.
   * @param {string} [params.customerId]
   * @param {() => void} [params.fallback]
   */
  deleteBookmark: async ({
    contentId,
    customerId,
    fallback = launchLoginModal,
  } = { contentId: 'undefined' }) => {
    if (!(await get().ensureAuthenticated())) {
      fallback();
      return false;
    }

    // optimistic remove flag
    set(
      // eslint-disable-next-line max-len
      (/** @type {MyNewsState} */ state) => ({ bookmarks: { ...state.bookmarks, [contentId]: false } }),
      false,
      'deleteBookmarkOptimistic',
    );

    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(`/mynewsapi/deleteBookmark/${contentId}/`, {
      customerId,
      isBedrockApiEnabled: get().isBedrockApiEnabled,
    });
    const { data } = response;

    if (!data.deleteBookmark) {
      await get().isContentBookmarked({ contentId, customerId });
      return false;
    }

    // purge from list
    set(
      produce((/** @type {MyNewsState} */ draft) => {
        draft.customer.bookmarks.data = draft.customer.bookmarks.data.filter(
          (bookmark) => bookmark.contentId !== contentId,
        );
      }),
      false,
      'deleteBookmarkRemoveFromList',
    );

    return true;
  },

  /**
   * Checks with the api whether the given content ID is bookmarked and updates the store with the result.
   *
   * @param {object} params
   * @param {string} params.contentId - The ID of the content to check for bookmarking.
   * @param {string} [params.customerId]
   * @async
   */
  isContentBookmarked: async ({ contentId, customerId }) => {
    // TODO: typing as any isn't ideal
    /** @type {any} */
    const response = await api(`/mynewsapi/iscontentbookmarked/${contentId}/`, {
      customerId,
      isBedrockApiEnabled: get().isBedrockApiEnabled,
    });
    set(
      (/** @type {MyNewsState} */ state) => ({
        bookmarks: {
          ...state.bookmarks,
          [contentId]: response.data.getBookmarkByContentId,
        },
      }),
      false,
      'isContentBookmarked',
    );
  },

  /**
   * Sets the isBedrockApiEnabled flag in the store.
   * @param {boolean} isBedrockApiEnabled
   */
  setIsBedrockApiEnabled: (isBedrockApiEnabled) => {
    set({ isBedrockApiEnabled }, false, 'setIsBedrockApiEnabled');
  },

});


/**
 * Enable the devtools in development mode only
 */

const isDevelop = process.env.NODE_ENV === 'development';

const devToolsProps = {
  name: 'useMyNews',
  anonymousActionType: 'action',
  serialize: true,
  /**
   * Sanitize the action type to include the store name
   * @param {{ type: string }} action
   */
  actionSanitizer: (action) => ({
    ...action,
    type: `MyNews/${action.type}`,
  }),
};

/**
 * Wrap store with redux devtools if in development mode
 * @param {Function} f - The function to apply the middleware to.
 */
const middlewares = (/** @type {any} */ f) => (isDevelop ? devtools(f, devToolsProps) : f);


/**
 * Create Store
 */
/**
 * @typedef {ReturnType<typeof store>} MyNewsState
 */

/**
 * @type {{
 *   <R>(selector: (state: MyNewsState) => R): R;
 *   getState: () => MyNewsState
 * }}
 */

export const useMyNewsStore = create(middlewares(store));
